import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { verifyEmail, resendVerification } from "@/services/auth.service";
import { useResendCooldown } from "@/hooks/useResendCooldown";
import posthog, { registerSession } from "@/lib/posthog";

const BROADCAST_CHANNEL = "blinkbooks_auth";
const ONBOARDING_PATH = "/onboarding/organization";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");
  const { cooldown, startCooldown } = useResendCooldown();

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendState("sending");
    try {
      await resendVerification(resendEmail);
      setResendState("sent");
      startCooldown();
    } catch {
      setResendState("idle");
    }
  };

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setError("Missing verification token.");
      return;
    }

    verifyEmail(token)
      .then((session) => {
        if (session?.user) {
          posthog.identify(session.user.id, {
            email: session.user.email,
            firstName: session.user.firstName,
            lastName: session.user.lastName,
          });
          registerSession();
        }
        // Notify any other open tab (e.g. the VerifyEmailPending page) that
        // verification is complete so it can navigate automatically.
        try {
          const bc = new BroadcastChannel(BROADCAST_CHANNEL);
          bc.postMessage({ type: "email_verified" });
          bc.close();
        } catch {
          // BroadcastChannel not supported — ignore
        }

        // Try to close this tab (works if the browser allows it).
        // Email clients open links in new tabs with no opener, so window.close()
        // is usually blocked. We show a "verified" state as a fallback.
        setVerified(true);
        window.close();

        // If still open after 400ms, navigate normally so the user isn't stuck.
        setTimeout(() => {
          navigate(ONBOARDING_PATH, { replace: true });
        }, 400);
      })
      .catch((err) => {
        setError(err?.message || "Verification failed. The link may have expired.");
      });
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4">
              <img src="/content/logo.png" alt="BlinkBooks Logo" className="w-full h-full object-contain" />
            </div>

            {verified ? (
              <>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Email verified!</h1>
                <p className="text-muted-foreground mb-6">
                  You can close this tab — your other tab has been updated automatically.
                </p>
                <button
                  onClick={() => navigate(ONBOARDING_PATH, { replace: true })}
                  className="text-sm text-primary underline hover:text-primary/80 transition-colors"
                >
                  Continue here instead
                </button>
              </>
            ) : !error ? (
              <>
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-foreground mb-2">Verifying your email...</h1>
                <p className="text-muted-foreground">Please wait a moment.</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Verification failed</h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                {resendState === "sent" ? (
                  <p className="text-sm text-green-600 mb-4">New verification email sent — check your inbox.</p>
                ) : (
                  <div className="flex gap-2 mb-4">
                    <input
                      type="email"
                      placeholder="Enter your email to resend"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button
                      onClick={handleResend}
                      disabled={!resendEmail || resendState === "sending" || cooldown > 0}
                      className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {resendState === "sending" ? "Sending..." : cooldown > 0 ? `${cooldown}s` : "Resend"}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
